/**
 * GovernancePanel — layout-only orchestrator for the Governance tab
 * (RAISE-16886). Renders 4 independent sections; each manages its own
 * loading/error/403 state.
 *
 * Previous single-section implementation replaced per Fable adversarial
 * review findings (7 issues → 4-section redesign).
 */

import { useTranslation } from "react-i18next";
import { GovernancePulse } from "@/components/project/GovernancePulse";
import { DecisionLog } from "@/components/project/DecisionLog";
import { ComplianceStrip } from "@/components/project/ComplianceStrip";
import { ArtifactBrowser } from "@/components/project/ArtifactBrowser";

interface GovernancePanelProps {
  projectSlug: string;
}

/**
 * Governance tab panel (RAISE-16886). Four independent sections:
 * 1. GovernancePulse — composite score + KPI tiles + attention
 * 2. DecisionLog — ADR list with status pills and Sheet detail
 * 3. ComplianceStrip — 30-day story compliance indicators
 * 4. ArtifactBrowser — file tree, collapsed by default
 */
export function GovernancePanel({ projectSlug }: GovernancePanelProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-8" data-testid="governance-panel">
      {/* Section 1: Governance Pulse */}
      <section aria-label={t("projectPortal.governance.pulse.title", { defaultValue: "Governance Pulse" })}>
        <h2 className="text-base font-semibold mb-3">
          {t("projectPortal.governance.pulse.title", { defaultValue: "Governance Pulse" })}
        </h2>
        <GovernancePulse projectSlug={projectSlug} />
      </section>

      {/* Section 2: Decision Log */}
      <section aria-label={t("projectPortal.governance.decisionLog.title", { defaultValue: "Decision Log" })}>
        <h2 className="text-base font-semibold mb-3">
          {t("projectPortal.governance.decisionLog.title", { defaultValue: "Decision Log" })}
        </h2>
        <DecisionLog projectSlug={projectSlug} />
      </section>

      {/* Section 3: Compliance Strip */}
      <section aria-label={t("projectPortal.governance.compliance.title", { defaultValue: "Compliance" })}>
        <h2 className="text-base font-semibold mb-3">
          {t("projectPortal.governance.compliance.title", { defaultValue: "Compliance" })}
        </h2>
        <ComplianceStrip projectSlug={projectSlug} />
      </section>

      {/* Section 4: Artifact Browser (collapsed by default) */}
      <section aria-label={t("projectPortal.governance.artifactBrowser.title", { defaultValue: "Artifact Browser" })}>
        <ArtifactBrowser projectSlug={projectSlug} />
      </section>
    </div>
  );
}
